"use client";

import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { usePathname } from "next/navigation";
import { Menu, X, Sun, Moon, Sparkles, ChevronDown } from "lucide-react";
import * as LucideIcons from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  fetchGeneralSettings,
  fetchServices,
} from "@/store/slices/landingSlice";
import { toggleTheme } from "@/store/slices/themeSlice";
import { getValidImageUrl } from "@/utils/imageUtils";
import { slugify } from "@/utils/slugify";

const baseLinks = [
  { name: "Home", href: "/" },
  { name: "Portfolio", href: "/portfolio" },
  { name: "What Our Clients Say", href: "/testimonials" },
  { name: "About Us", href: "/about-us" },
  { name: "Contact Us", href: "/contact" },
];

const Navbar = () => {
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const { data: settings } = useAppSelector(
    (state) => state.landing.generalSettings,
  );
  const { data: services } = useAppSelector((state) => state.landing.services);
  const { mode } = useAppSelector((state) => state.theme);

  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const [scrolled, setScrolled] = useState(false);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isDark = mounted && mode === "dark";

  useEffect(() => {
    setMounted(true);
  }, []);

  const settingsData = settings as typeof settings & {
    hearderlogolight?: string;
    hearderlogodark?: string;
  };
  const activeHeaderLogo = isDark
    ? settingsData?.hearderlogodark ||
      settingsData?.hearderlogolight ||
      settings?.headerLogo
    : settingsData?.hearderlogolight ||
      settingsData?.hearderlogodark ||
      settings?.headerLogo;
  const headerLogoUrl = getValidImageUrl(activeHeaderLogo);

  const isLinkActive = (href: string) => {
    if (!pathname) return false;
    return pathname === href || pathname.startsWith(`${href}/`);
  };

  const isServicesActive = pathname?.startsWith("/services");

  const announcements = [
    "Welcome to Hubmicroo — Transforming ideas into powerful digital products.",
    "Limited Time: Book your strategy call and kickstart faster delivery.",
    "New Case Studies: See how we helped brands scale with confidence.",
    "Need support now? Our team is available to guide your next move.",
  ];

  useEffect(() => {
    dispatch(fetchGeneralSettings());
    dispatch(fetchServices());
  }, [dispatch]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAnnouncement((prev) => (prev + 1) % announcements.length);
    }, 4200);
    return () => clearInterval(interval);
  }, [announcements.length]);

  useEffect(() => {
    setIsOpen(false);
    setMobileServicesOpen(false);
  }, [pathname]);

  // Handle dropdown hover with slight delay to prevent accidental dismissal
  const handleServicesEnter = () => {
    if (dropdownTimerRef.current) clearTimeout(dropdownTimerRef.current);
    setShowServicesDropdown(true);
  };
  const handleServicesLeave = () => {
    dropdownTimerRef.current = setTimeout(
      () => setShowServicesDropdown(false),
      180,
    );
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed cursor-pointer top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isDark
          ? scrolled
            ? "bg-[#030712]/95 border-b border-white/10 backdrop-blur-xl"
            : "bg-[#030712]/85 border-b border-white/10 backdrop-blur-lg"
          : scrolled
            ? "bg-white/95 border-b border-slate-200 backdrop-blur-xl"
            : "bg-white/90 border-b border-slate-200/80 backdrop-blur-lg"
      }`}
    >
      {/* Announcement bar */}
      <div
        className={`relative overflow-hidden border-b ${
          isDark
            ? "border-white/10 bg-gradient-to-r from-[#111827] via-[#1f1a40] to-[#111827]"
            : "border-slate-200 bg-gradient-to-r from-[#f7f5ff] via-[#eef2ff] to-[#f8fafc]"
        }`}
      >
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="top-0 left-1/4 absolute bg-[#8B31FF]/40 blur-2xl rounded-full w-32 h-32" />
          <div className="right-1/4 bottom-0 absolute bg-[#8B31FF]/30 blur-2xl rounded-full w-32 h-32" />
        </div>
        <div className="relative mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <div className="flex justify-between items-center gap-3 h-10">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${isDark ? "bg-white/10 text-white" : "bg-white text-[#8B31FF] shadow-sm"}`}
              >
                <Sparkles size={12} />
                Updates
              </span>
              <div className="min-w-0">
                <AnimatePresence mode="wait">
                  <motion.p
                    key={currentAnnouncement}
                    initial={{ y: 10, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -10, opacity: 0 }}
                    transition={{ duration: 0.35 }}
                    className={`text-xs sm:text-sm font-medium truncate ${isDark ? "text-slate-100" : "text-slate-700"}`}
                  >
                    {announcements[currentAnnouncement]}
                  </motion.p>
                </AnimatePresence>
              </div>
            </div>
            <Link
              href="/contact"
              className={`shrink-0 text-xs sm:text-sm font-semibold transition-colors ${isDark ? "text-white hover:text-[#C4B5FD]" : "text-[#8B31FF] hover:text-[#3f0d9c]"}`}
            >
              Talk to us →
            </Link>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-8xl">
        <div className="flex justify-between items-center gap-3 h-20">
          {/* Logo */}
          <Link href="/" className="shrink-0">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="flex items-center gap-2 cursor-pointer"
            >
              {headerLogoUrl ? (
                <img
                  src={headerLogoUrl}
                  alt="Hubmicroo logo"
                  className="h-10 md:h-12 w-auto object-contain"
                />
              ) : (
                <div className="flex justify-center items-center bg-gradient-to-r from-[#8B31FF] to-[#8B31FF] rounded-lg w-10 h-10">
                  <span className="font-bold text-white text-xl">H</span>
                </div>
              )}
              <span
                className={`text-xl md:text-2xl font-semibold transition-colors ${isDark ? "text-white" : "text-slate-900"} ${headerLogoUrl ? "hidden sm:inline" : "inline"}`}
              />
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div
            className={`hidden xl:flex items-center p-1.5 rounded-lg border ${isDark ? "border-white/10 bg-white/5" : "border-slate-200 bg-slate-100/70"}`}
          >
            {/* Home */}
            <Link href="/">
              <motion.div
                whileHover={{ y: -2 }}
                className={`px-4 py-2 mx-1 rounded-lg font-medium text-sm transition-all cursor-pointer ${
                  isLinkActive("/")
                    ? isDark
                      ? "bg-white/10 text-white"
                      : "bg-white text-slate-900 shadow-sm"
                    : isDark
                      ? "text-slate-300 hover:text-white hover:bg-white/10"
                      : "text-slate-600 hover:text-slate-900 hover:bg-white"
                }`}
              >
                Home
              </motion.div>
            </Link>

            {/* Services dropdown */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleServicesEnter}
              onMouseLeave={handleServicesLeave}
            >
              <motion.div
                whileHover={{ y: -2 }}
                className={`px-4 py-2 mx-1 rounded-lg font-medium text-sm transition-all cursor-pointer flex items-center gap-1.5 ${
                  isServicesActive
                    ? isDark
                      ? "bg-white/10 text-white"
                      : "bg-white text-slate-900 shadow-sm"
                    : isDark
                      ? "text-slate-300 hover:text-white hover:bg-white/10"
                      : "text-slate-600 hover:text-slate-900 hover:bg-white"
                }`}
              >
                Services
                <motion.span
                  animate={{ rotate: showServicesDropdown ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown size={14} />
                </motion.span>
              </motion.div>

              {/* Dropdown panel */}
              <AnimatePresence>
                {showServicesDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.97 }}
                    transition={{ duration: 0.18 }}
                    onMouseEnter={handleServicesEnter}
                    onMouseLeave={handleServicesLeave}
                    className={`absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[560px] rounded-lg border shadow-2xl overflow-hidden z-50 ${
                      isDark
                        ? "bg-[#0B0F19] border-white/10"
                        : "bg-white border-gray-100"
                    }`}
                  >
                    {/* Dropdown header */}
                    <div
                      className={`px-5 py-4 border-b ${isDark ? "border-white/8 bg-[#030712]/60" : "border-gray-50 bg-gray-50/80"}`}
                    >
                      <p
                        className={`text-xs font-semibold uppercase tracking-widest ${isDark ? "text-gray-500" : "text-gray-400"}`}
                      >
                        Our Services
                      </p>
                      <p
                        className={`text-sm font-medium mt-0.5 ${isDark ? "text-gray-300" : "text-gray-700"}`}
                      >
                        Everything you need to grow digitally
                      </p>
                    </div>

                    {/* Services grid */}
                    <div className="p-3 grid grid-cols-2 gap-1">
                      {services.map((service, i) => {
                        const SIcon =
                          (LucideIcons as any)[service.icon] || LucideIcons.Zap;
                        const href = `/services/${slugify(service.title)}`;
                        const active = pathname === href;
                        return (
                          <Link
                            key={service._id || i}
                            href={href}
                            onClick={() => setShowServicesDropdown(false)}
                          >
                            <motion.div
                              whileHover={{ x: 3 }}
                              className={`flex items-start gap-3 px-3 py-3 rounded-lg transition-all duration-200 cursor-pointer ${
                                active
                                  ? isDark
                                    ? "bg-[#8B31FF]/20 text-[#C4B5FD]"
                                    : "bg-purple-50 text-[#8B31FF]"
                                  : isDark
                                    ? "hover:bg-white/5 text-gray-300"
                                    : "hover:bg-gray-50 text-gray-700"
                              }`}
                            >
                              <div
                                className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${isDark ? "bg-[#8B31FF]/20" : "bg-purple-50"}`}
                              >
                                <SIcon
                                  className={`w-4 h-4 ${isDark ? "text-[#8B31FF]" : "text-[#8B31FF]"}`}
                                />
                              </div>
                              <div className="min-w-0">
                                <p
                                  className={`text-sm font-semibold leading-tight ${isDark ? "text-white" : "text-gray-900"}`}
                                >
                                  {service.title}
                                </p>
                                <p
                                  className={`text-xs mt-0.5 leading-snug truncate ${isDark ? "text-gray-500" : "text-gray-500"}`}
                                >
                                  {service.description}
                                </p>
                              </div>
                            </motion.div>
                          </Link>
                        );
                      })}
                    </div>

                    {/* View all */}
                    <div
                      className={`px-4 py-3 border-t ${isDark ? "border-white/8" : "border-gray-50"}`}
                    >
                      <Link
                        href="/#services"
                        onClick={() => setShowServicesDropdown(false)}
                      >
                        <div
                          className={`flex items-center gap-2 text-xs font-semibold transition-colors ${isDark ? "text-[#8B31FF] hover:text-[#C4B5FD]" : "text-[#8B31FF] hover:text-[#6D28D9]"}`}
                        >
                          <Sparkles size={12} />
                          View all services on our home page →
                        </div>
                      </Link>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Rest of nav links */}
            {baseLinks.slice(1).map((link, index) => (
              <Link key={index} href={link.href}>
                <motion.div
                  whileHover={{ y: -2 }}
                  className={`px-4 py-2 mx-1 rounded-lg font-medium text-sm transition-all cursor-pointer ${
                    isLinkActive(link.href)
                      ? isDark
                        ? "bg-white/10 text-white"
                        : "bg-white text-slate-900 shadow-sm"
                      : isDark
                        ? "text-slate-300 hover:text-white hover:bg-white/10"
                        : "text-slate-600 hover:text-slate-900 hover:bg-white"
                  }`}
                >
                  {link.name}
                </motion.div>
              </Link>
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden xl:flex items-center gap-2">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => dispatch(toggleTheme())}
              className={`p-2.5 cursor-pointer rounded-full transition-all duration-300 ${isDark ? "bg-white/10 text-white hover:bg-white/20" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              aria-label="Toggle theme"
            >
              {isDark ? <Sun size={18} /> : <Moon size={18} />}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`px-5 py-2.5 cursor-pointer rounded-lg font-semibold transition-all shadow-md ${isDark ? "bg-white hover:bg-slate-100 text-slate-900 shadow-white/10" : "bg-[#872ffb] hover:bg-[#7526e3] text-white shadow-purple-500/20"}`}
              onClick={() => router.push("/contact")}
            >
              Get Started
            </motion.button>
          </div>

          {/* Mobile controls */}
          <div className="xl:hidden flex items-center gap-1">
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => dispatch(toggleTheme())}
              className={`p-2 cursor-pointer rounded-lg ${isDark ? "text-white hover:bg-white/10" : "text-slate-700 hover:bg-slate-100"}`}
              aria-label="Toggle theme"
            >
              {isDark ? <Sun size={21} /> : <Moon size={21} />}
            </motion.button>
            <motion.button
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 cursor-pointer rounded-lg ${isDark ? "text-white hover:bg-white/10" : "text-slate-700 hover:bg-slate-100"}`}
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </motion.button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className={`xl:hidden border-t overflow-hidden ${isDark ? "border-white/10 bg-[#030712]/95 backdrop-blur-xl" : "border-slate-200 bg-white"}`}
          >
            <div className="space-y-1 px-4 sm:px-8 md:px-12 py-5 max-w-2xl md:mx-auto">
              {/* Home */}
              <Link href="/">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-3 rounded-lg font-medium transition-colors cursor-pointer ${
                    isLinkActive("/")
                      ? isDark
                        ? "bg-white/10 text-white"
                        : "bg-slate-100 text-slate-900"
                      : isDark
                        ? "text-slate-300 hover:bg-white/10 hover:text-white"
                        : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                >
                  Home
                </motion.div>
              </Link>

              {/* Services expand/collapse */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 }}
              >
                <button
                  onClick={() => setMobileServicesOpen((p) => !p)}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-lg font-medium transition-colors cursor-pointer ${
                    isServicesActive
                      ? isDark
                        ? "bg-white/10 text-white"
                        : "bg-slate-100 text-slate-900"
                      : isDark
                        ? "text-slate-300 hover:bg-white/10 hover:text-white"
                        : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                >
                  <span>Services</span>
                  <motion.span
                    animate={{ rotate: mobileServicesOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown size={16} />
                  </motion.span>
                </button>

                <AnimatePresence>
                  {mobileServicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.25 }}
                      className={`overflow-hidden ml-4 mt-1 rounded-lg border ${isDark ? "border-white/8 bg-white/3" : "border-gray-100 bg-gray-50"}`}
                    >
                      <div className="py-2 px-2 grid grid-cols-1 sm:grid-cols-2 gap-0.5">
                        {services.map((service, i) => {
                          const SIcon =
                            (LucideIcons as any)[service.icon] ||
                            LucideIcons.Zap;
                          const href = `/services/${slugify(service.title)}`;
                          return (
                            <Link
                              key={service._id || i}
                              href={href}
                              onClick={() => setIsOpen(false)}
                            >
                              <motion.div
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.04 }}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer ${
                                  pathname === href
                                    ? isDark
                                      ? "bg-[#8B31FF]/20 text-[#C4B5FD]"
                                      : "bg-purple-50 text-[#8B31FF]"
                                    : isDark
                                      ? "hover:bg-white/8 text-gray-300"
                                      : "hover:bg-white text-gray-700"
                                }`}
                              >
                                <SIcon
                                  className={`w-4 h-4 flex-shrink-0 ${isDark ? "text-[#8B31FF]" : "text-[#8B31FF]"}`}
                                />
                                <span className="text-sm font-medium">
                                  {service.title}
                                </span>
                              </motion.div>
                            </Link>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Rest of links */}
              {baseLinks.slice(1).map((link, index) => (
                <Link key={index} href={link.href}>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: (index + 2) * 0.05 }}
                    onClick={() => setIsOpen(false)}
                    className={`block px-4 py-3 rounded-lg font-medium transition-colors cursor-pointer ${
                      isLinkActive(link.href)
                        ? isDark
                          ? "bg-white/10 text-white"
                          : "bg-slate-100 text-slate-900"
                        : isDark
                          ? "text-slate-300 hover:bg-white/10 hover:text-white"
                          : "text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                    }`}
                  >
                    {link.name}
                  </motion.div>
                </Link>
              ))}

              <div className="pt-3">
                <motion.button
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: (baseLinks.length + 1) * 0.05 }}
                  className={`w-full px-6 py-3 rounded-lg font-semibold transition-all shadow-md ${isDark ? "bg-white hover:bg-slate-100 text-slate-900 shadow-white/10" : "bg-[#872ffb] hover:bg-[#7526e3] text-white shadow-purple-500/20"}`}
                  onClick={() => {
                    setIsOpen(false);
                    router.push("/contact");
                  }}
                >
                  Get Started
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
